import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import { api, getErrorMessage, getToken, setToken } from "@/lib/api";

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
    const [user, setUser] = useState(null);   // null = checking, false = anon, object = signed in
    const [loading, setLoading] = useState(true);

    const checkAuth = useCallback(async () => {
        try {
            const { data } = await api.get("/auth/me");
            setUser(data);
        } catch {
            if (getToken()) setToken(null);
            setUser(false);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        // CRITICAL: If returning from OAuth callback, skip the /me check.
        // AuthCallback will exchange the session_id and establish the session first.
        if (typeof window !== "undefined" && window.location.hash?.includes("session_id=")) {
            setLoading(false);
            return;
        }
        checkAuth();
    }, [checkAuth]);

    const login = async (email, password) => {
        const { data } = await api.post("/auth/login", { email, password });
        if (data.access_token) setToken(data.access_token);
        const { access_token, refresh_token, ...userOnly } = data;
        setUser(userOnly);
        return userOnly;
    };

    const register = async (name, email, password) => {
        const { data } = await api.post("/auth/register", { name, email, password });
        // Do NOT set token or user here — user must verify email first
        return data;
    };

    const loginWithGoogle = async (credential) => {
        const { data } = await api.post("/auth/google", { credential });
        if (data.access_token) setToken(data.access_token);
        const { access_token, refresh_token, ...userOnly } = data;
        setUser(userOnly);
        return userOnly;
    };
    
    const verifyEmail = async (token) => {
        const { data } = await api.post("/auth/verify", { token });
        if (data.access_token) setToken(data.access_token);
        const { access_token, refresh_token, ...userOnly } = data;
        setUser(userOnly);
        return userOnly;
    };

    const logout = async () => {
        try { await api.post("/auth/logout"); } catch {}
        setToken(null);
        setUser(false);
    };

    const refresh = async () => { await checkAuth(); };

    return (
        <AuthContext.Provider value={{ user, loading, login, loginWithGoogle, verifyEmail, register, logout, refresh, getErrorMessage, setToken }}>
            {children}
        </AuthContext.Provider>
    );
};

export const useAuth = () => {
    const ctx = useContext(AuthContext);
    if (!ctx) throw new Error("useAuth must be inside AuthProvider");
    return ctx;
};
